import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * PathwayDiagram - SVG-based pathway visualization with animated particle flow.
 * All rendering is done in SVG to avoid canvas/SVG coordinate mismatches.
 */
const PathwayDiagram = ({ scenario, simState, advancedMode, isRunning, totalDose }) => {
  const [particles, setParticles] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [nodeSizes, setNodeSizes] = useState({});
  const resizeRef = useRef(null);
  const svgRef = useRef(null);
  const particleIdRef = useRef(0);

  const layout = scenario.pathwayLayout;

  const getNodeSize = (nodeId) => {
    return nodeSizes[nodeId] || { w: 110, h: 44 };
  };

  // Resize handlers
  const onResizeStart = useCallback((e, nodeId, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    const size = nodeSizes[nodeId] || { w: 110, h: 44 };
    resizeRef.current = { nodeId, handle, startX: svgP.x, startY: svgP.y, startW: size.w, startH: size.h };

    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const mp = svg.createSVGPoint();
      mp.x = ev.clientX;
      mp.y = ev.clientY;
      const sp = mp.matrixTransform(svg.getScreenCTM().inverse());
      const dx = sp.x - resizeRef.current.startX;
      const dy = sp.y - resizeRef.current.startY;
      const r = resizeRef.current;
      let newW = r.startW;
      let newH = r.startH;
      if (r.handle.includes('r')) newW = Math.max(70, r.startW + dx * 2);
      if (r.handle.includes('l')) newW = Math.max(70, r.startW - dx * 2);
      if (r.handle.includes('b')) newH = Math.max(30, r.startH + dy * 2);
      if (r.handle.includes('t')) newH = Math.max(30, r.startH - dy * 2);
      setNodeSizes(prev => ({ ...prev, [r.nodeId]: { w: newW, h: newH } }));
    };
    const onUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [nodeSizes]);

  const getNodeLabel = useCallback((node) => {
    if (!advancedMode) {
      const mol = scenario.molecules.find(m => m.id === node.id);
      if (mol) return mol.simpleName || mol.name;
      const enz = scenario.enzymes.find(e => e.id === node.id);
      if (enz) return enz.simpleName || enz.name;
      if (node.moleculeId) {
        const linked = scenario.molecules.find(m => m.id === node.moleculeId);
        return linked?.simpleName || linked?.name || node.id;
      }
    }
    if (node.moleculeId) return node.moleculeId;
    const enz = scenario.enzymes.find(e => e.id === node.id);
    if (enz) return enz.name;
    return node.id;
  }, [advancedMode, scenario]);

  const getNodeColor = useCallback((node) => {
    const mol = scenario.molecules.find(m => m.id === node.id);
    if (mol) return mol.color;
    if (node.moleculeId) {
      const linked = scenario.molecules.find(m => m.id === node.moleculeId);
      return linked?.color || '#999';
    }
    if (node.type === 'enzyme') {
      // Check if this enzyme is used by any toxic reaction
      const isToxicEnzyme = scenario.reactions.some(
        r => r.enzyme === node.id && r.toxic
      );
      return isToxicEnzyme ? '#D0021B' : '#2E7D32';
    }
    return '#999';
  }, [scenario]);

  const isMinorEnzyme = useCallback((nodeId) => {
    return scenario.reactions.some(
      r => r.enzyme === nodeId && r.toxic && r.minor
    );
  }, [scenario]);

  // Spawn and update particles based on simulation state
  useEffect(() => {
    if (!simState || !isRunning) return;

    const newParticles = [];
    for (const edge of layout.edges) {
      const reaction = scenario.reactions.find(r => r.id === edge.reactionId);
      if (!reaction) continue;

      const substrate = simState.molecules[reaction.substrate];
      if (!substrate || substrate.amount <= 0.1) continue;

      const enzyme = simState.enzymes?.[reaction.enzyme];
      const vmax = reaction.vmax || reaction.maxRate || 1;
      const km = reaction.km || 10;
      const activityMult = enzyme?.activityMultiplier || 1;
      // M-M rate as fraction of Vmax, scaled for spawn probability
      const rate = vmax * activityMult * substrate.amount / (km + substrate.amount);
      let spawnChance = Math.min(0.9, rate / 1.5);
      // Ensure toxic reactions always show some particles when substrate exists
      if (reaction.toxic && substrate.amount > 0.5) {
        spawnChance = Math.max(0.3, spawnChance);
      }

      if (Math.random() < spawnChance) {
        const fromNode = layout.nodes.find(n => n.id === edge.from);
        const toNode = layout.nodes.find(n => n.id === edge.to);
        if (fromNode && toNode) {
          particleIdRef.current++;
          newParticles.push({
            id: particleIdRef.current,
            fromX: fromNode.x + (Math.random() - 0.5) * 10,
            fromY: fromNode.y + (Math.random() - 0.5) * 10,
            toX: toNode.x + (Math.random() - 0.5) * 10,
            toY: toNode.y + (Math.random() - 0.5) * 10,
            color: reaction.toxic ? '#FF4444' : '#44DD44',
            size: 3 + Math.random() * 3,
            duration: 0.8 + Math.random() * 0.6,
            born: Date.now(),
          });
        }
      }
    }

    setParticles(prev => [
      ...prev.filter(p => Date.now() - p.born < p.duration * 1000),
      ...newParticles,
    ].slice(-80));
  }, [simState, layout, scenario, isRunning]);

  // Clean up old particles periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setParticles(prev => prev.filter(p => Date.now() - p.born < p.duration * 1000));
    }, 200);
    return () => clearInterval(interval);
  }, []);

  const getMolDisplay = (nodeId) => {
    const mol = simState?.molecules[nodeId];
    const node = layout.nodes.find(n => n.id === nodeId);
    const resolved = mol || (node?.moleculeId ? simState?.molecules[node.moleculeId] : null);
    if (!resolved) return null;

    // For products, show as approximate % of total dose
    if (totalDose > 0 && (resolved.category === 'product_safe' || nodeId === 'APAP_GSH')) {
      const pct = (resolved.amount / totalDose) * 100;
      if (pct < 0.1) return null;
      return '~' + pct.toFixed(0) + '% of dose';
    }

    // For APAP (substrate), show remaining as % of dose
    if (resolved.id === 'APAP' && totalDose > 0) {
      const pct = (resolved.amount / totalDose) * 100;
      return '~' + pct.toFixed(0) + '% remaining';
    }

    // For cofactors, show as % of max (1 decimal to show small changes)
    if (resolved.category === 'cofactor') {
      const pct = (resolved.amount / (resolved.maxAmount || 1)) * 100;
      return '~' + pct.toFixed(1) + '%';
    }

    // For NAPQI (toxic), show raw amount
    if (resolved.category === 'toxic') {
      if (resolved.amount < 0.05) return null;
      return '~' + resolved.amount.toFixed(1);
    }

    // For protein adducts (damage), show raw amount
    if (resolved.category === 'damage') {
      if (resolved.amount < 0.05) return null;
      return '~' + resolved.amount.toFixed(1);
    }

    return null;
  };

  const getGlowIntensity = (nodeId) => {
    if (!simState) return 0;
    const mol = simState.molecules[nodeId];
    if (!mol) return 0;
    if (mol.category === 'toxic') return Math.min(1, mol.amount / 15);
    if (mol.category === 'damage') return Math.min(1, mol.amount / 10);
    return 0;
  };

  // Dynamic node size based on amount
  const getNodeScale = (nodeId) => {
    if (!simState || !totalDose) return 1;
    const node = layout.nodes.find(n => n.id === nodeId);
    const mol = simState.molecules[nodeId]
      || (node?.moleculeId ? simState.molecules[node.moleculeId] : null);
    if (!mol) return 1;

    // For APAP (substrate): scale from 1.0 down to 0.3 as it's consumed
    if (mol.id === 'APAP') {
      const pct = mol.amount / totalDose;
      return 0.3 + 0.7 * pct;
    }

    // For products: scale from 0.3 up to 1.3 as they accumulate
    if (mol.category === 'product_safe' || nodeId === 'APAP_GSH') {
      const pct = Math.min(1, mol.amount / totalDose);
      return 0.3 + 1.0 * pct;
    }

    // For toxic intermediates: scale from 0.5 up to 1.5
    if (mol.category === 'toxic') {
      const pct = Math.min(1, mol.amount / 20);
      return 0.5 + 1.0 * pct;
    }

    // Protein adducts (damage): scale from 0.3 up to 1.5
    if (mol.category === 'damage') {
      const pct = Math.min(1, mol.amount / 15);
      return 0.3 + 1.2 * pct;
    }

    // Cofactors: scale from 0.5 to 1.0 based on remaining amount
    if (mol.category === 'cofactor') {
      const pct = mol.amount / (mol.maxAmount || 1);
      return 0.5 + 0.5 * pct;
    }

    return 1;
  };

  return (
    <div className="pathway-diagram">
      <svg ref={svgRef} viewBox="0 0 800 560" style={{ width: '100%', height: '100%' }}>
        <defs>
          <filter id="glow-red">
            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="glow-green">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <marker id="arrowhead" markerWidth="10" markerHeight="7"
            refX="10" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#555" />
          </marker>
          <marker id="arrowhead-red" markerWidth="10" markerHeight="7"
            refX="10" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#D0021B" />
          </marker>
        </defs>

        {/* Background grid */}
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1e2a4a" strokeWidth="0.5" />
        </pattern>
        <rect width="800" height="560" fill="url(#grid)" />

        {/* Edges */}
        {layout.edges.map((edge, i) => {
          const from = layout.nodes.find(n => n.id === edge.from);
          const to = layout.nodes.find(n => n.id === edge.to);
          if (!from || !to) return null;

          const reaction = scenario.reactions.find(r => r.id === edge.reactionId);
          const isToxic = reaction?.toxic;
          const isMinor = reaction?.minor;

          // Calculate absolute rate for this reaction
          let rate = 0;
          if (reaction && simState) {
            const substrate = simState.molecules[reaction.substrate];
            const enzyme = simState.enzymes?.[reaction.enzyme];
            if (substrate?.amount > 0.1) {
              const vmax = reaction.vmax || reaction.maxRate || 1;
              const km = reaction.km || 10;
              const actMult = enzyme?.activityMultiplier || 1;
              rate = vmax * actMult * substrate.amount / (km + substrate.amount);
            }
          }

          // Normalize against the highest possible rate (UGT vmax=4.0)
          const activity = Math.min(1, rate / 4.0);
          // Ensure all pathways are visible with minimum sizes
          const baseWidth = isMinor ? 0.8 : 1.2;
          const activityWidth = activity * 1.5;

          const midX = (from.x + to.x) / 2;
          const midY = (from.y + to.y) / 2;

          return (
            <g key={i}>
              <line
                x1={from.x} y1={from.y}
                x2={to.x} y2={to.y}
                stroke={isToxic ? `rgba(208, 2, 27, ${0.35 + activity * 0.5})` : `rgba(100, 130, 180, ${0.25 + activity * 0.5})`}
                strokeWidth={baseWidth + activityWidth}
                strokeDasharray={isMinor ? '6 4' : 'none'}
                markerEnd={isToxic ? 'url(#arrowhead-red)' : 'url(#arrowhead)'}
              />
              {edge.from === 'SULT' && edge.to === 'APAP_sulfate' && (
                <text x={midX - 12} y={midY - 6}
                  fontSize={8} fill="#7aba7a" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  Sulfation
                </text>
              )}
              {edge.from === 'UGT' && edge.to === 'APAP_glucuronide' && (
                <text x={midX - 20} y={midY - 6}
                  fontSize={8} fill="#7aba7a" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  Glucuronidation
                </text>
              )}
              {edge.from === 'PAPS_node' && edge.to === 'SULT' && (
                <text x={midX + 6} y={midY - 4}
                  fontSize={7} fill="#BD10E0" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  Sulfur donor
                </text>
              )}
              {edge.from === 'UDPGA_node' && edge.to === 'UGT' && (
                <text x={midX - 5} y={midY - 4}
                  fontSize={7} fill="#8B572A" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  Glucuronic acid donor
                </text>
              )}
              {edge.from === 'APAP' && (edge.to === 'UGT' || edge.to === 'SULT') && (
                <text x={midX - 12} y={midY - 6}
                  fontSize={8} fill="#7aba7a" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  Phase II
                </text>
              )}
              {edge.from === 'APAP' && edge.to === 'CYP2E1' && (
                <text x={midX + 6} y={midY - 6}
                  fontSize={8} fill="#e07070" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  Phase I
                </text>
              )}
              {edge.from === 'NAPQI' && edge.to === 'APAP_GSH' && (
                <text x={midX - 30} y={midY - 6}
                  fontSize={7} fill="#4ECDC4" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  GSH available
                </text>
              )}
              {edge.from === 'NAPQI' && edge.to === 'PROTEIN_ADDUCTS' && (
                <text x={midX + 6} y={midY - 6}
                  fontSize={7} fill="#FF4444" fontStyle="italic"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>
                  GSH depleted
                </text>
              )}
            </g>
          );
        })}

        {/* Animated particles */}
        {particles.map(p => {
          const elapsed = (Date.now() - p.born) / 1000;
          const t = Math.min(1, elapsed / p.duration);
          const x = p.fromX + (p.toX - p.fromX) * t;
          const y = p.fromY + (p.toY - p.fromY) * t;
          const opacity = t < 0.1 ? t * 10 : t > 0.8 ? (1 - t) * 5 : 1;

          return (
            <circle
              key={p.id}
              cx={x}
              cy={y}
              r={p.size}
              fill={p.color}
              opacity={opacity * 0.8}
            />
          );
        })}

        {/* Nodes */}
        {layout.nodes.map((node) => {
          const color = getNodeColor(node);
          const label = getNodeLabel(node);
          const amount = getMolDisplay(node.id);
          const glow = getGlowIntensity(node.id);
          const isEnzyme = node.type === 'enzyme';
          const isCofactor = node.type === 'cofactor';
          const scale = getNodeScale(node.id);

          const handleNodeClick = (e) => {
            e.stopPropagation();
            const mol = scenario.molecules.find(m => m.id === node.id)
              || (node.moleculeId ? scenario.molecules.find(m => m.id === node.moleculeId) : null);
            const enz = scenario.enzymes.find(en => en.id === node.id);
            const item = mol || enz;
            if (!item || !item.description) return;
            if (tooltip && tooltip.id === node.id) {
              setTooltip(null);
              return;
            }
            setTooltip({
              id: node.id,
              name: item.name,
              description: item.description,
              x: node.x,
              y: node.y,
              color,
            });
          };

          return (
            <g key={node.id} transform={`translate(${node.x}, ${node.y})`}
              onClick={handleNodeClick} style={{ cursor: 'pointer' }}>
              {/* Glow effect for toxic molecules */}
              {glow > 0.05 && (
                <circle r={28 + glow * 15} fill="none" stroke="#FF2200"
                  strokeWidth={1 + glow * 4} opacity={glow * 0.6} filter="url(#glow-red)" />
              )}

              {/* Node shape */}
              {isEnzyme ? (() => {
                const sz = getNodeSize(node.id);
                const hw = sz.w / 2;
                const hh = sz.h / 2;
                return (
                  <>
                    <rect x={-hw} y={-hh} width={sz.w} height={sz.h} rx={8}
                      fill={color} fillOpacity={0.15} stroke={color} strokeWidth={2}
                      strokeDasharray={isMinorEnzyme(node.id) ? '6 3' : 'none'} />
                    {/* Resize handles at corners */}
                    {[
                      { cx: hw, cy: -hh, h: 'rt' },
                      { cx: hw, cy: hh, h: 'rb' },
                      { cx: -hw, cy: -hh, h: 'lt' },
                      { cx: -hw, cy: hh, h: 'lb' },
                    ].map(({ cx, cy, h }) => (
                      <circle key={h} cx={cx} cy={cy} r={4}
                        fill="#2a3a5c" stroke="#8ab4f8" strokeWidth={1}
                        style={{ cursor: (h.includes('r') && h.includes('b')) || (h.includes('l') && h.includes('t')) ? 'nwse-resize' : 'nesw-resize' }}
                        onMouseDown={(e) => onResizeStart(e, node.id, h)} />
                    ))}
                  </>
                );
              })() : isCofactor ? (
                <circle r={22 * scale} fill={color} fillOpacity={0.12} stroke={color}
                  strokeWidth={2} strokeDasharray="5 3" />
              ) : (
                <circle r={26 * scale} fill={color} fillOpacity={0.15} stroke={color} strokeWidth={2} />
              )}

              {/* Label */}
              {(() => {
                const baseFontSize = node.id === 'APAP' ? 12 : 10;
                // CYP enzymes: name — descriptor (e.g. "CYP2E1 — Toxic (major)")
                const emParts = label.split(' \u2014 ');
                if (emParts.length > 1 && isEnzyme) {
                  return (
                    <>
                      <text textAnchor="middle" dy={-5}
                        fontSize={baseFontSize} fill="#d0d0d0" fontWeight="700"
                        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                        {emParts[0]}
                      </text>
                      <text textAnchor="middle" dy={10}
                        fontSize={8} fill="#aaa" fontWeight="400"
                        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                        {emParts[1]}
                      </text>
                    </>
                  );
                }
                // UGT: two-line full name (e.g. "UDP - glucuronosyltransferase")
                const hyphenParts = label.split(' - ');
                if (hyphenParts.length > 1 && isEnzyme) {
                  return hyphenParts.map((line, i) => (
                    <text key={i} textAnchor="middle" dy={-6 + i * 14}
                      fontSize={baseFontSize} fill="#d0d0d0" fontWeight="600"
                      style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                      {line}
                    </text>
                  ));
                }
                // Molecule two-line labels (e.g. "Glucuronide\nSafe product")
                const nlParts = label.split('\n');
                if (nlParts.length > 1) {
                  return (
                    <>
                      <text textAnchor="middle" dy={-10}
                        fontSize={baseFontSize} fill="#d0d0d0" fontWeight="700"
                        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                        {nlParts[0]}
                      </text>
                      <text textAnchor="middle" dy={3}
                        fontSize={8} fill="#aaa" fontWeight="400"
                        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                        {nlParts[1]}
                      </text>
                    </>
                  );
                }
                return (
                  <text textAnchor="middle" dy={isEnzyme ? 1 : -4}
                    fontSize={baseFontSize} fill="#d0d0d0" fontWeight="600"
                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                    {label}
                  </text>
                );
              })()}

              {/* Enzyme sub-label */}
              {isEnzyme && (
                <text textAnchor="middle" dy={32}
                  fontSize={8} fill="#888" fontStyle="italic"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                  enzyme
                </text>
              )}

              {/* Amount */}
              {amount !== null && (() => {
                // Check if this is a cofactor node and add down arrow when depleting
                const mol = simState?.molecules[node.id]
                  || (node.moleculeId ? simState?.molecules[node.moleculeId] : null);
                let arrow = '';
                if (mol?.category === 'cofactor') {
                  const pct = mol.amount / (mol.maxAmount || 1);
                  if (pct < 0.95) arrow = ' \u25BC'; // ▼ down arrow
                }
                const isLow = mol?.category === 'cofactor'
                  && mol.amount / (mol.maxAmount || 1) < 0.4;
                return (
                  <text textAnchor="middle" dy={isEnzyme ? 18 : 15}
                    fontSize={12} fill={isLow ? '#FF6B6B' : '#ccc'} fontWeight="700"
                    fontFamily="monospace"
                    style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                    {amount}{arrow}
                  </text>
                );
              })()}
            </g>
          );
        })}

        {/* Damage overlay — red vignette when health is low */}
        {simState?.damage?.hepatocyte_health && simState.damage.hepatocyte_health.value < 60 && (
          <rect width="800" height="560" fill="none"
            stroke="#FF0000"
            strokeWidth={Math.max(0, (60 - simState.damage.hepatocyte_health.value) / 3)}
            opacity={Math.min(0.6, (60 - simState.damage.hepatocyte_health.value) / 80)}
            rx={10}
          />
        )}
      </svg>

      {/* Clickable tooltip overlay */}
      {tooltip && (
        <div className="node-tooltip"
          style={{
            left: tooltip.x < 200
              ? `${(tooltip.x / 800) * 100 + 18}%`
              : `${(tooltip.x / 800) * 100}%`,
            top: tooltip.x < 200
              ? `${(tooltip.y / 560) * 100 - 25}%`
              : `${(tooltip.y / 560) * 100}%`,
            transform: tooltip.x < 200
              ? 'translate(0, -100%)'
              : 'translate(-50%, 30px)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tooltip-header" style={{ borderLeftColor: tooltip.color }}>
            <strong>{tooltip.name}</strong>
            <button className="tooltip-close" onClick={() => setTooltip(null)}>&times;</button>
          </div>
          <div className="tooltip-desc">
            {tooltip.description.split('\n\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}

      {/* Click away to dismiss tooltip */}
      {tooltip && (
        <div className="tooltip-backdrop" onClick={() => setTooltip(null)} />
      )}
    </div>
  );
};

export default PathwayDiagram;
